import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import formAPI from "../../api/formApi";
import TableShimmer from "../../components/partials/TableShimmer";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";
import { formatDate } from "../../helper/Helper";

function Submission() {
  const { formId } = useParams();
  const [tableLoading, setTableLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [formData, setFormData] = useState([]); // State for store list
  const [pagination, setPagination] = useState({}); // State for pagination info
  const [searchParams, setSearchParams] = useSearchParams();

  const query = React.useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      status: searchParams.get("status") || null,
      search: searchParams.get("search") || null,
    }),
    [searchParams],
  );

  const fetchSubmissionData = useCallback(async () => {
    setTableLoading(true);
    setFormData([]);
    setPagination({});

    try {
      const params = searchParams;

      console.log(formId);

      if (formId) {
        params.set("formId", formId);
      } else {
        params.delete("formId");
      }

      const res = await formAPI.getSubmissionList(params);
      if (res.code === 200) {
        const { pagination, submissions } = res.data;

        setFormData(submissions ?? []);
        setPagination(pagination ?? {});
      }
    } catch {
    } finally {
      setTableLoading(false);
    }
  }, [searchParams, formId]);

  useEffect(() => {
    fetchSubmissionData();
  }, [fetchSubmissionData]);

  const searchTimeout = useRef();
  const handleSearch = (value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateFilter("search", value);
    }, 400);
  };

  const updateFilter = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);
      value ? params.set(key, value) : params.delete(key);

      if (key !== "page") {
        params.set("page", 1);
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const getBasicDetail = (answers) => {
    if (!answers) return "N/A";
    const keys = Object.keys(answers);
    const primaryKey = keys.find(
      (k) =>
        k.toLowerCase().includes("email") ||
        k.toLowerCase().includes("name") ||
        k.toLowerCase().includes("phone"),
    );
    return primaryKey
      ? String(answers[primaryKey])
      : String(answers[keys[0]] || "N/A");
  };

  // Helper function: Check karne ke liye ki value koi image URL hai ya nahi
  const isImageUrl = (url) => {
    if (typeof url !== "string") return false;
    return (
      url.match(/\.(jpeg|jpg|gif|png|webp|svg)($|\?)/i) != null ||
      url.startsWith("data:image/")
    );
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Submission List</h4>
              <h6>View and manage all form filled by customers</h6>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            {pagination.page && (
              <div className="row">
                <div className="col-md-12">
                  <div className="countablepagination p-2">
                    <p>
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total,
                      )}{" "}
                      of {pagination.total} results
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>S. No.</th>
                    <th>Form Name</th>
                    <th>Primary Detail</th>
                    <th>ip Hash</th>
                    <th>Created At</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={10} cols={6} />
                  ) : formData.length > 0 ? (
                    formData.map((form, index) => (
                      <tr key={form._id || index}>
                        <td>{(query.page - 1) * query.limit + index + 1}.</td>
                        <td>
                          <Link
                            to={`/submissions/${form?.formId?._id}`}
                            className="text-primary fw-bold"
                          >
                            {form?.formId?.name || "Unnamed Form"}
                          </Link>
                        </td>
                        {/* 🟢 Table mein lambe answers dikhane ke badle short basic info */}
                        <td>
                          <span
                            className="text-truncate d-inline-block"
                            style={{ maxWidth: "200px" }}
                          >
                            {getBasicDetail(form.answers)}
                          </span>
                        </td>
                        <td>
                          <code className="text-muted">
                            {form.ipHash || "N/A"}
                          </code>
                        </td>
                        <td>{formatDate(form.createdAt)}</td>
                        {/* 🟢 Action View Button */}
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            data-bs-toggle="modal"
                            data-bs-target="#submissionDetailsModal"
                            onClick={() => setSelectedSubmission(form)}
                          >
                            <i className="fa fa-eye me-1"></i> View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center p-4 text-muted">
                        No submission till now
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.page && (
              <div className="row">
                <RowsPerPage
                  value={searchParams.get("limit") || "10"}
                  onChange={(val) => updateFilter("limit", val)}
                />

                <Pagination
                  pagination={pagination}
                  updateFilter={updateFilter}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="submissionDetailsModal"
        tabIndex="-1"
        aria-labelledby="submissionDetailsModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="delete-modalLabel">
                Submission Details ({selectedSubmission?.formId?.name || "Form"}
                )
              </h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              {selectedSubmission ? (
                <div>
                  <div className="row g-3 mb-3 p-3 bg-light rounded border">
                    <div className="col-md-6">
                      <small className="text-muted d-block">Submitted At</small>
                      <strong>
                        {formatDate(selectedSubmission.createdAt)}
                      </strong>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted d-block">
                        IP Hash Address
                      </small>
                      <code>{selectedSubmission.ipHash || "N/A"}</code>
                    </div>
                  </div>

                  <h6 className="fw-bold mb-3 border-bottom pb-2">
                    Form Responses
                  </h6>
                  <div className="qa-container">
                    <div className="row">
                      {Object.entries(selectedSubmission.answers || {}).length >
                      0 ? (
                        Object.entries(selectedSubmission.answers).map(
                          ([key, value]) => (
                            <div className="col-md-6" key={key}>
                              <div className="mb-2 p-2 rounded border-start border-primary border-4 bg-white shadow-sm">
                                <span className="text-muted small text-uppercase fw-bold d-block mb-1">
                                  {key.replace(/_/g, " ")}
                                </span>

                                {/* 🟢 IMAGE PREVIEW LOGIC: Agar URL image ka hai toh render karega warna normal text */}
                                {isImageUrl(value) ? (
                                  <div className="mt-2">
                                    <div className="mb-2">
                                      <img
                                        src={String(value)}
                                        alt={key}
                                        className="img-thumbnail rounded"
                                        style={{
                                          maxHeight: "200px",
                                          objectFit: "contain",
                                        }}
                                      />
                                    </div>
                                    <a
                                      href={String(value)}
                                      target="_blank"
                                      rel="noreferrer noopener"
                                      className="btn btn-xs btn-light border text-primary small"
                                    >
                                      <i className="fa fa-external-link me-1"></i>{" "}
                                      Open Original Image
                                    </a>
                                  </div>
                                ) : (
                                  <div
                                    className="text-dark fw-medium"
                                    style={{ whiteSpace: "pre-wrap" }}
                                  >
                                    {String(value || "—")}
                                  </div>
                                )}
                              </div>
                            </div>
                          ),
                        )
                      ) : (
                        <p className="text-muted">
                          No key-value answers recorded.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                  ></div>
                  <p className="text-muted mt-2">Loading details...</p>
                </div>
              )}
            </div>

            <div className="modal-footer bg-light">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-shadow="none"
                data-bs-dismiss="modal"
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Submission;
